import type { Profile } from "@/hooks/useAuth";

/**
 * ⏸️ IL BANCO E' IN STANDBY (Davide, 08/09/2026, prima del lancio).
 *
 * «Tutto il codice che riguarda il banco per l'assistenza umana deve essere
 * messo in standby.» Il motivo non e' che funziona male: e' che la v1 parte
 * con l'assistente, e l'assistenza umana entra in scena solo quando avra' una
 * regola sua — quella scritta nella ROTTA del primo aggiornamento: dopo tre o
 * quattro risposte che non risolvono, l'installatore puo' passare alle persone
 * di Datacom, che rispondono dal lunedi' al venerdi' (orari in orarioBanco.ts).
 *
 * COSA VUOL DIRE «STANDBY», QUI. Il codice resta tutto dov'e' — pagine, chat,
 * pannello, tabelle, policy: non si cancella niente, perche' quello che si
 * cancella si riscrive. Si spegne la PORTA, in un punto solo: questa funzione.
 * Con l'interruttore a `true` nessuno vede il banco: ne' l'installatore a cui
 * era stato concesso, ne' l'operatore Datacom, ne' l'admin. Le voci di menu'
 * spariscono, le due pagine rimandano indietro, la scheda del pannello non si
 * apre.
 *
 * COME SI RIACCENDE: si mette `BANCO_IN_STANDBY = false` e si ripubblica.
 * Nient'altro. Prima di farlo, due cose da sistemare (annotate nella rotta):
 * il passaggio automatico dopo N risposte insufficienti, e la prova sul
 * telefono della schermata del banco (il difetto della tacca dell'iPhone,
 * corretto l'08/09 ma mai visto su un dispositivo vero).
 *
 * 🔛 RIACCESO PROVVISORIAMENTE (Davide, 23/09/2026). La porta e' aperta, ma
 * resta una CONCESSIONE per cliente (`bancoConcesso`): nessun installatore lo
 * vede finche' Datacom non glielo accende dal pannello, e chi lo vede scrive
 * solo con l'account attivo. Le due cose da sistemare qui sopra restano da
 * fare. Per rispegnerlo: `true` e ripubblica.
 */
export const BANCO_IN_STANDBY = false;

/**
 * Chi vede (e usa) la chat col banco Datacom.
 *
 * Dal 06/10/2026 (Davide): il banco e' «invisibile». L'installatore lo vede
 * SOLO mentre ha una richiesta di tecnico attiva, cioe' dopo aver chiesto una
 * persona in chat, e lo perde quando il caso e' chiuso; per tornarci richiede
 * un tecnico in chat. La concessione dal pannello (`banco_abilitato`, 01/09)
 * non apre piu' il banco da sola: se servisse di nuovo, e' la riga commentata
 * qui sotto. La voce di menu', la scheda negli strumenti e la pagina usano
 * tutti QUESTA regola; il database tiene la sua copia nella policy di
 * scrittura (banco_aperto_per).
 */
export function bancoConcesso(
  profile: Profile | null,
  isAdmin: boolean,
  /** Ha una richiesta di tecnico aperta (l'ultimo salvagente): la porta e' aperta per quel caso. */
  richiestaAperta = false,
): boolean {
  if (BANCO_IN_STANDBY) return false;
  if (isAdmin || profile?.banco_operatore) return true;
  if (richiestaAperta) return true;
  // Prima del 06/10 (con import { statoAccesso } from "@/lib/statoAccesso"):
  // return profile?.banco_abilitato === true && statoAccesso(profile) === "abbonato";
  return false;
}

/**
 * Chi RISPONDE al banco. Davide, 24/09/2026: «al banco tutti gli account
 * @datacomtecnologie possono rispondere» — non serve promuovere nessuno.
 * Stessa regola di `utenteInterno` nella funzione chat e di
 * `e_operatore_banco()` nel database (migrazione del 24/09).
 */
export const DOMINI_INTERNI = ["@datacomtecnologie.it", "@b2brand.it"];

export function utenteInterno(email: string | null | undefined): boolean {
  const e = (email ?? "").toLowerCase();
  return !!e && DOMINI_INTERNI.some((d) => e.endsWith(d));
}

export function eOperatoreBanco(profile: Profile | null, isAdmin: boolean, email: string | null | undefined): boolean {
  if (BANCO_IN_STANDBY) return false;
  return isAdmin || profile?.banco_operatore === true || utenteInterno(email);
}

/**
 * La regola dal 06/10/2026 (audit S7): admin o spunta «operatore» data da un
 * admin, e basta. E' quella che usa il database dopo la migrazione
 * 20261006010000; il menu la usa solo finche' il database non ha risposto
 * (useBanco chiede e_operatore_banco).
 */
export function eOperatoreBancoLocale(profile: Profile | null, isAdmin: boolean): boolean {
  if (BANCO_IN_STANDBY) return false;
  return isAdmin || profile?.banco_operatore === true;
}

/**
 * Il banco e' governabile dal pannello admin? In standby no: una scheda che
 * concede un privilegio che poi non esiste e' peggio di una scheda assente.
 */
export function bancoGovernabile(): boolean {
  return !BANCO_IN_STANDBY;
}
